import React, { useState, useEffect, useCallback } from 'react';
import {
  TitleRow,
  ContainerRow,
  SearchInputStyle,
  ControlsDiV
} from '../styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import { useSelector } from 'react-redux';
import PageTitle from '../../../Utils/PageTitle';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import {
  RESULTS_PER_PAGE,
  SEARCH_DELAY
} from '../../../Constants/StringConstants';
import { createAdminColumns } from './tanstackColumnConfig';
import AddAdminForm from './AddAdminForm';
import CustomSideDrawer from '../../CustomComponents/CustomSideDrawer';
import { listAll, saveAdmin, editAdmin } from '../../../Apis/Admin';
import { getBillerAssignmentPicker } from '../../../Apis/Biller';
import { notification, Modal } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useDebounce } from '../../../Utils/Debounce';
import { AddButton } from '../styledComponents';
import { exportToExcel } from '../../../Utils/ExcelExport';
import { reInviteUser } from '../../../Apis/User';
import FacilityFilter from '../../CustomComponents/FacilityFilter';
import { getUserFacilities, getReviewers } from '../../../Apis/User';
import { disableOrEnableUser } from '../../../Apis/ClinicAdmin';

const Admins = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [searchString, setSearchString] = useState('');
  const [total, setTotal] = useState(0);
  const [admins, setAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterKey, setFilterKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('');
  const [facilityIds, setFacilityIds] = useState([]);
  const [facilities, setFacilities] = useState();
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const userData = useSelector(state => state.userReducer);
  const notClinicAdmin = userData.userType !== 'clinicAdmin' && userData.userType !== 'admin';
  const [reviewersList, setReviewersList] = useState([]);
  const [billersList, setBillersList] = useState([]);
  const [billerGroupsList, setBillerGroupsList] = useState([]);
  const billerConfigChanged = (oldConfig, newConfig) => {
    const sort = arr => [...(arr || [])].sort().join(',');
    return (
      sort(oldConfig?.billers) !== sort(newConfig?.billers) ||
      sort(oldConfig?.billerGroups) !== sort(newConfig?.billerGroups)
    );
  };

  const submitHandler = values => {
    // values.reviewerList = values.reviewerList.includes('none')
    //   ? []
    //   : values.reviewerList;
    const payload = values;
    values.phoneNumber.number = values.phoneNumber.number.replace(
      values.phoneNumber.countryCode,
      ''
    );
    if (selectedItem) {
      payload.id = selectedItem.id;

      const doSave = (updateBillerConfigForExistingJobs = false) => {
        setSubmitLoading(true);
        const params = updateBillerConfigForExistingJobs
          ? { updateBillerConfigForExistingJobs: true, sessionId: userData.sessionId }
          : {};
        editAdmin(payload, params)
          .then(response => {
            if (response.data.success) {
              notification['success']({
                message: 'Updated',
                description: 'Admin details updated.'
              });
              setSubmitLoading(false);
              setAddDrawerVisible(false);
              setSelectedItem(null);
              fetchAll();
            } else {
              notification['error']({
                message: 'Failed',
                description: response.data.message
              });
              setSubmitLoading(false);
            }
          })
          .catch(err => {
            setSubmitLoading(false);
            console.log(err);
          });
      };

      if (billerConfigChanged(selectedItem.billerConfig, payload.billerConfig)) {
        Modal.confirm({
          title: 'Update existing jobs?',
          content: (
            <>
              <style>{`.biller-confirm-modal .ant-modal-content { border-radius: 8px; }`}</style>
              Do you want to assign all unbilled jobs to the new biller ?
            </>
          ),
          className: 'biller-confirm-modal',
          style: { top: 250 },
          okText: 'Yes',
          cancelText: 'No',
          okButtonProps: { style: { borderRadius: 8 } },
          cancelButtonProps: { style: { borderRadius: 8 } },
          onOk: () => doSave(true),
          onCancel: () => doSave(false)
        });
      } else {
        doSave(false);
      }
    } else {
      saveAdmin(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Added',
              description: 'Admin added successfully'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
            setCurrentPage(0);
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    }
  };

  const getFacilities = () => {
    getUserFacilities()
      .then(response => {
        if (response.data.success) {
          setFacilities(
            response.data.result.map(item => {
              return {
                label: item.facilityName,
                value: item.facilityId,
                disabled: item.disabled,
                className: item.disabled ? 'disabled-facility-option' : ''
              };
            })
          );
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => console.log(err));
  };

  const disableUser = id => {
    disableOrEnableUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
          fetchAll();
          setCurrentPage(0);
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const reInvite = id => {
    reInviteUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const fetchAll = () => {
    setLoading(true);
    // Simplified API call - backend sends all items without pagination params
    listAll()
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          setTotal(response.data.size);
          setAdmins(response.data.result);
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        setLoading(false);
      });
    getReviewers()
      .then(response => {
        if (response.data.success) {
          setReviewersList(response.data.result);
        }
      })
      .catch(() => {
        // Reviewers fetch error - don't affect main loading state
      });
    getBillerAssignmentPicker()
      .then(response => {
        if (response.data.success) {
          const result = response.data.result[0] || {};
          setBillersList(result.billers || []);
          setBillerGroupsList(result.billerGroups || []);
        }
      })
      .catch(() => {
        // Picker fetch error - don't affect main loading state
      });
  };

  const filterTableChange = (pagination, filters, sorter) => {
    if (sorter.field !== undefined)
      setFilterKey(sorter.field && sorter.field[0]);
    if (sorter.order !== undefined) setSortOrder(Boolean(sorter.order));
  };

  const onChangeHandler = value => {
    setCurrentPage(0);
    setSearchString(value);
  };

  const debounceOnChange = useCallback(
    useDebounce(onChangeHandler, SEARCH_DELAY),
    []
  );

  const handleExport = () => {
    // Apply filters to data
    let filteredData = admins;

    // Apply facility filter
    if (facilityIds && facilityIds.length > 0) {
      filteredData = filteredData.filter(admin =>
        admin.facilities.some(facility =>
          facilityIds.includes(facility.facilityId)
        )
      );
    }

    // Apply search filter
    if (searchString) {
      const searchLower = searchString.toLowerCase();
      filteredData = filteredData.filter(admin => {
        return (
          (admin.fullName &&
            admin.fullName.toLowerCase().includes(searchLower)) ||
          (admin.email && admin.email.toLowerCase().includes(searchLower)) ||
          (admin.phoneNumber &&
            `${admin.phoneNumber.countryCode}${admin.phoneNumber.number}`.includes(
              searchLower
            ))
        );
      });
    }

    // Custom export columns
    const exportColumns = [
      { id: 'id', header: 'ID', accessorKey: 'id' },
      { id: 'fullName', header: 'Name', accessorKey: 'fullName' },
      { id: 'email', header: 'Email', accessorKey: 'email' },
      { id: 'phoneNumber', header: 'Phone Number', accessorKey: 'phoneNumber' },
      { id: 'facility', header: 'Facility', accessorKey: 'facilities' },
      { id: 'reviewerLevel', header: 'Reviewer Level', accessorKey: 'reviewerFlow' },
      { id: 'reviewersForLevel1', header: 'Reviewers', accessorKey: 'reviewerFlow' },
      { id: 'dictationType', header: 'Dictation Type', accessorKey: 'dictationType' },
      { id: 'createdAt', header: 'Created At', accessorKey: 'createdAt' }
    ];

    exportToExcel(filteredData, exportColumns, 'Admins');
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []); // Fetch all data once on mount - filtering is done client-side

  useEffect(() => {
    getFacilities();
  }, []);

  return (
    <>
      <PageTitle title='Doctors' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}></CustomTypography>
          <ControlsDiV>
            <AddButton
              icon={<PlusOutlined />}
              type='primary'
              onClick={() => setAddDrawerVisible(true)}
            >
              Add
            </AddButton>
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              value={searchString}
              onChange={event => {
                setSearchString(event.target.value);
                debounceOnChange(event.target.value);
              }}
            />
            <AddButton
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export to Excel
            </AddButton>
            <AddButton
              onClick={() => {
                setSearchString('');
                setClearFiltersTrigger(t => t + 1);
              }}
            >
              Clear Filter
            </AddButton>
            {/* <FacilityFilter setSelectedValues={setFacilityIds} /> */}
          </ControlsDiV>
        </TitleRow>
        <TanStackUserTable
          data={admins}
          columns={createAdminColumns(
            setSelectedItem,
            setAddDrawerVisible,
            disableUser,
            reInvite,
            notClinicAdmin
          )}
          loading={loading}
          searchValue={searchString}
          storageKey='users_page_admins'
          clearFiltersTrigger={clearFiltersTrigger}
          customFilterFn={
            facilityIds && facilityIds.length > 0
              ? admin => {
                  // Filter by facility IDs if facilities are selected
                  return admin.facilities.some(facility =>
                    facilityIds.includes(facility.facilityId)
                  );
                }
              : null
          }
        />

        <CustomSideDrawer
          title={selectedItem ? 'Edit Admin' : 'Add Admin'}
          placement='right'
          closable={false}
          onClose={() => {
            setSelectedItem(null);
            setAddDrawerVisible(false);
          }}
          visible={addDrawerVisible}
          width={400}
          destroyOnClose={true}
        >
          <AddAdminForm
            facilities={facilities}
            selectedItem={selectedItem}
            submitHandler={submitHandler}
            reviewerList={reviewersList}
            billersList={billersList}
            billerGroupsList={billerGroupsList}
            submitLoading={submitLoading}
          />
        </CustomSideDrawer>
      </ContainerRow>
    </>
  );
};

export default Admins;
